import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { required, email, length } from 'redux-form-validators';
import { isValidNumber } from 'libphonenumber-js';

import { setNonAuthorized, setAuthorized } from 'auth/login/_redux';
import { Agreements, ReduxTextField, ReduxSelectField, PhoneField, DefaultButton } from 'common/components';
import { withTheme } from 'common/styling/theme';
import { isAgreementAutoAcknowledgedBroker } from 'common/utils';
import TelegramAuthWidget from '../../../_components/TelegramAuthWidget';

import { loginWithTelegramFromReg, registration, togglePartnerCodeAction, togglePromoCodeAction } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

// TODO: Вынести этот функционал в отдельный пункт меню админки
const isEnvi = document.location.hostname === 'trade.envifx.com';

const validatePhoneNumber = phoneNumber => {
  if (!isValidNumber(phoneNumber)) {
    return <FormattedMessage id="validationErrorWrongPhone" />;
  }
};

const validate = values => {
  const errors = {};

  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).+$/;
  if (!passwordRegex.test(values.password)) {
    errors.password = <FormattedMessage id="form.errors.passwordValidate" />;
  }

  if (values.passwordRepeat !== values.password) {
    errors.passwordRepeat = <FormattedMessage id="form.errors.passwordRepeat" />; // eslint-disable-line
  }

  if (isEnvi ? !/^\d*$/.test(values.partnerCode) : !/^\d+$/.test(values.partnerCode)) {
    errors.partnerCode = <FormattedMessage id="form.errors.partnerCode" />; // eslint-disable-line
  }

  return errors;
};

function RegistrationForm({
  partnership,
  handleSubmit,
  submitting,
  invalid,
  error,
  partnerCodeIsOpen,
  promoCodeIsOpen,
  isCorporate,
  disclamer,
  requirePartnerCode,
  togglePartnerCode,
  togglePromoCode,
  theme,
  registrationSettings,
  customRegistrationFormOnebid,
  initialValues,
  dispatch,
  isTelegramAuth,
  telegramData,
  loginWithTelegramFromReg,
  countries,
  countriesAreLoaded,
  personalIdInKycForm,
  selectedClientType,
  intl,
  brokerId,
}) {
  const [passwordFieldType, setPasswordFieldType] = useState('password');
  const agreementAutoAcknowledged = isAgreementAutoAcknowledgedBroker(brokerId);
  const [agreementConfirmed, setAgreementConfirmed] = useState(agreementAutoAcknowledged);

  // 指定 broker（如 10/229）自动确认协议：即使 broker 配置晚于组件渲染到达，也同步勾选状态
  useEffect(() => {
    if (agreementAutoAcknowledged) {
      setAgreementConfirmed(true);
    }
  }, [agreementAutoAcknowledged]);

  const showPassword = () => {
    if (passwordFieldType === 'password') {
      setPasswordFieldType('text');
    } else {
      setPasswordFieldType('password');
    }
  };

  useEffect(() => {
    if (!partnerCodeIsOpen && (initialValues.partnerCode || requirePartnerCode)) {
      togglePartnerCode();
    }
  }, []);

  const submit = handleSubmit(values => {
    const element = document.getElementById('registration-page-partner-code');
    let isPartnerCodeCustomlyHidden = false;
    if (element) {
      const style = window.getComputedStyle(element);
      isPartnerCodeCustomlyHidden = style.getPropertyValue('display') === 'none';
    }
    const { partnerCode, ...data } = values;
    let sendData = partnerCodeIsOpen ? { ...values, isPartnerCodeCustomlyHidden } : data;

    if (isEnvi && !partnerCode) {
      delete sendData.partnerCode;
    }

    // 确保sales_code被包含在发送的数据中
    if (values.salesCode) {
      sendData.salesCode = values.salesCode;
    }

    if (values.share) {
      sendData.share = values.share;
    }

    if (isTelegramAuth && telegramData) sendData = { ...sendData, ...telegramData };

    if (customRegistrationFormOnebid && !promoCodeIsOpen && sendData.promoCode) {
      delete sendData.promoCode;
    }

    return registration({ ...sendData }, dispatch);
  });

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const handleTelegramAuth = data =>
    loginWithTelegramFromReg({ ...data, ...(initialValues.share ? { share: initialValues.share } : {}) });

  const countriesOptions = countries.map(option => ({
    value: option.gid,
    label: option.name,
  }));

  const clientTypeOptions = [
    { value: 'new', label: intl.formatMessage({ id: 'authPersonalAccount' }) },
    { value: 'corporate', label: intl.formatMessage({ id: 'authCorporateAccount' }) },
  ];

  return (
    <form id="main-registration-form" className="RegistrationForm" onSubmit={submit}>
      {/* temp for tixee */}
      {window.location.hostname !== 'my.tixee.com' && disclamer?.enabled && disclamer?.subject && (
        <div className="disclamer__text">{disclamer.subject}</div>
      )}
      {isCorporate && (
        <Field
          className="RegistrationForm__select-client-type"
          component={ReduxSelectField}
          name="clientType"
          textId="justClientType"
          options={clientTypeOptions}
          disabled={submitting}
          validate={[required()]}
        />
      )}
      {!customRegistrationFormOnebid && (
        <div className="RegistrationForm__inputs-inner">
          <Field
            component={ReduxTextField}
            type="text"
            name="firstName"
            textId="justFirstName"
            validate={[required()]}
            disabled={submitting}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="lastName"
            textId="justLastName"
            validate={[required()]}
            disabled={submitting}
          />
        </div>
      )}
      <Field
        component={ReduxTextField}
        type="email"
        name="email"
        textId={selectedClientType === 'corporate' ? 'authCompanyEmail' : 'authEmail'}
        validate={[required(), email()]}
        disabled={submitting}
      />
      {!customRegistrationFormOnebid && (
        <Field
          component={PhoneField}
          name="phone"
          textId="justPhone"
          validate={[required(), validatePhoneNumber]}
          disabled={submitting}
        />
      )}

      <Field
        className="RegistrationForm__select-nationality"
        component={ReduxSelectField}
        searchable
        name="nationality"
        textId="kycNationality"
        options={countriesOptions}
        disabled={submitting || !countriesAreLoaded}
        validate={[required()]}
      />
      {customRegistrationFormOnebid && (
        <Field
          className="RegistrationForm__select-country"
          component={ReduxSelectField}
          searchable
          name="countryGid"
          textId="justCountryResidence"
          options={countriesOptions}
          disabled={submitting || !countriesAreLoaded}
          validate={[required()]}
        />
      )}
      {personalIdInKycForm && !customRegistrationFormOnebid && (
        <>
          <Field
            className="RegistrationForm__select-country"
            component={ReduxSelectField}
            searchable
            name="countryGid"
            textId="justCountryResidence"
            options={countriesOptions}
            disabled={submitting || !countriesAreLoaded}
            validate={[required()]}
          />

          <Field
            component={ReduxTextField}
            type="text"
            name="personalId"
            textId="kycPassportNumber"
            disabled={submitting}
            validate={[required()]}
          />
        </>
      )}
      {!isTelegramAuth && (
        <div className="RegistrationForm__inputs-inner">
          <Field
            component={ReduxTextField}
            type={passwordFieldType}
            name="password"
            textId="authPassword"
            validate={[required(), length({ min: 6 })]}
            disabled={submitting}
            action={showPassword}
            actionTooltipId={passwordFieldType === 'password' ? 'showPassword' : 'hidePassword'}
            actionClassName={passwordFieldType === 'password' ? 'show' : 'hide'}
          />
          <Field
            component={ReduxTextField}
            type="password"
            name="passwordRepeat"
            textId="authPasswordRepeat"
            validate={[required()]}
            disabled={submitting}
          />
        </div>
      )}
      {selectedClientType === 'corporate' && (
        <Field
          id="registration-page-company-name"
          component={ReduxTextField}
          type="text"
          name="companyName"
          textId="authCompanyName"
          validate={[required()]}
          disabled={submitting}
        />
      )}
      {partnership.enabled && partnerCodeIsOpen && (
        <>
          <Field
            id="registration-page-partner-code"
            component={ReduxTextField}
            type="text"
            name="partnerCode"
            textId="authPartnerCodeField"
            validate={[...(isEnvi ? [] : [required()])]}
            disabled={submitting}
          />
          {/* 添加隐藏的sales_code输入框 */}
          <Field
            component={ReduxTextField}
            type="hidden"
            name="salesCode"
            initialValue={initialValues.salesCode || ''}
          />
        </>
      )}
      <Field component={ReduxTextField} type="hidden" name="share" initialValue={initialValues.share || ''} />
      {promoCodeIsOpen && customRegistrationFormOnebid && (
        <Field
          id="registration-page-promo-code"
          component={ReduxTextField}
          type="text"
          name="promoCode"
          textId="authPromoCodeField"
          validate={[required()]}
          disabled={submitting}
        />
      )}
      {partnership.enabled && !requirePartnerCode && !isEnvi && !initialValues?.salesCode && (
        <div id="registration-page-partner-code-inner" className="PartnerCode__inner">
          <FormattedMessage id={partnerCodeIsOpen ? 'authNoPartnerCode' : 'authPartnerCode'}>
            {txt => (
              <button type="button" onClick={togglePartnerCode}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        </div>
      )}
      {customRegistrationFormOnebid && (
        <div id="registration-page-promo-code-inner" className="PartnerCode__inner">
          <FormattedMessage id={promoCodeIsOpen ? 'authNoPromoCode' : 'authPromoCode'}>
            {txt => (
              <button type="button" onClick={togglePromoCode}>
                {txt}
              </button>
            )}
          </FormattedMessage>
        </div>
      )}
      <div
        className="RegistrationForm__agreement-checkbox"
        style={agreementAutoAcknowledged ? { display: 'none' } : {}}>
        <input
          type="checkbox"
          id="agreementConfirmed"
          checked={agreementConfirmed}
          onChange={() => setAgreementConfirmed(prev => !prev)}
        />
        <label htmlFor="agreementConfirmed" className="agreement-label">
          <span className="agreement-check-box">
            <svg viewBox="0 0 14 11" fill="none">
              <polyline
                points="1.5 6 4.5 9 10.5 1"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="agreement-check-text">
            <FormattedMessage
              id="authAgreementConfirmation"
              values={{
                link: (
                  <a
                    href="/agreements/Comprehensive-Agreements.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}>
                    <FormattedMessage id="authComprehensiveAgreements" />
                  </a>
                ),
              }}
            />
          </span>
        </label>
      </div>
      <Agreements agreements={registrationSettings.agreements} submitting={submitting} />
      {/* temp for tixie */}
      {window.location.hostname === 'my.tixee.com' && disclamer?.enabled && disclamer?.subject && (
        <div className="disclamer__text disclamer__text--noMG">{disclamer.subject}</div>
      )}
      <DefaultButton
        id="registration-page-registration-button"
        textId="authDoRegister"
        type="submit"
        className="regForm__submit-button"
        disabled={invalid || submitting || !agreementConfirmed}
        loading={submitting}
        filled
      />
      <TelegramAuthWidget handleTelegramAuth={handleTelegramAuth} />
      {error && <strong className="formError">{error}</strong>}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx>{dynamicStyles}</style>
    </form>
  );
}

RegistrationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  error: PropTypes.string,
  theme: PropTypes.object,
  partnership: PropTypes.object.isRequired,
  partnerCodeIsOpen: PropTypes.bool,
  promoCodeIsOpen: PropTypes.bool,
  disclamer: PropTypes.object,
  requirePartnerCode: PropTypes.bool.isRequired,
  togglePartnerCode: PropTypes.func.isRequired,
  togglePromoCode: PropTypes.func.isRequired,
  registrationSettings: PropTypes.object.isRequired,
  customRegistrationFormOnebid: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  countries: PropTypes.array,
  countriesAreLoaded: PropTypes.bool.isRequired,
  personalIdInKycForm: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    partnerCode: PropTypes.string,
    salesCode: PropTypes.string, // 添加sales_code到propTypes
    share: PropTypes.string,
  }),
  isTelegramAuth: PropTypes.bool.isRequired,
  telegramData: PropTypes.object.isRequired,
  loginWithTelegramFromReg: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  selectedClientType: PropTypes.string,
  isCorporate: PropTypes.bool,
  brokerId: PropTypes.number,
};

RegistrationForm.defaultProps = {
  error: '',
  selectedClientType: 'new',
  theme: {},
  disclamer: {},
  partnerCodeIsOpen: false,
  promoCodeIsOpen: false,
  initialValues: {},
  countries: [],
  isCorporate: false,
  brokerId: null,
};

const selector = formValueSelector('registration');

export default compose(
  injectIntl,
  withTheme(),
  withRouter,
  connect(
    state => ({
      selectedClientType: selector(state, 'clientType'),
      brokerId: state.interfaceConfig.brokerId,
      registrationSettings: state.interfaceConfig.registration,
      partnership: state.interfaceConfig.partnership,
      partnerCodeIsOpen: isEnvi ? true : state.auth.registration.partnerCodeIsOpen,
      promoCodeIsOpen: state.auth.registration.promoCodeIsOpen,
      requirePartnerCode: state.interfaceConfig.requirePartnerCode,
      disclamer: state.interfaceConfig.disclamer,
      personalIdInKycForm: state.interfaceConfig.features.includes('personalIdInKycForm'),
      customRegistrationFormOnebid: state.interfaceConfig.features.includes('customRegistrationFormOnebid'),
    }),
    dispatch => ({
      togglePartnerCode: () => dispatch(togglePartnerCodeAction()),
      togglePromoCode: () => dispatch(togglePromoCodeAction()),
      loginWithTelegramFromReg: data => dispatch(loginWithTelegramFromReg.request(data)),
    })
  ),
  reduxForm({
    form: 'registration',
    validate,
    onSubmitSuccess: (
      result,
      dispatch,
      { history, isEmailConfirmationEnabled, displayThankBlock, displayTfaModal, isTfaRequired }
    ) => {
      // Этот элемент указывает, что регистрация прошла успешно
      const divElement = document.createElement('div');

      divElement.id = 'div-that-indicates-successful-registration';

      divElement.style.width = '1px';
      divElement.style.height = '1px';
      divElement.style.display = 'none !important';

      document.body.appendChild(divElement);

      if (isTfaRequired && !isEmailConfirmationEnabled) {
        displayTfaModal();
        dispatch(setNonAuthorized());
      }

      if (isEmailConfirmationEnabled) {
        displayThankBlock();
      }

      if (!isTfaRequired && !isEmailConfirmationEnabled) {
        dispatch(setAuthorized());
        history.push('/');
      }
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })
)(RegistrationForm);
export { RegistrationForm };
